<template>
  <BoxWithTitle
    collapsible
    openingIcon="mdi-chevron-down"
    closingIcon="mdi-chevron-up"
  >
    <template #header>
      <v-toolbar-title>
        {{ $t('standards.couponStandards') }}
      </v-toolbar-title>
    </template>
    <template #content>
      <v-skeleton-loader
        v-if="loading"
        class="m-auto"
        type="table"
        transition="scale-transition"
      />
      <VTableLocal
        v-else-if="hasPermission('standards.view') && hasPermission('standardsFamilies.view') && hasPermission('analyses.types.view')"
        :iconHeader="hasPermission('standards.add')? 'add' : null"
        :items="displayedStandards"
        isSearchable
        :headers="tableHeaders"
        :itemActions="actions"
        :title="$t('standards.couponStandards')"
        @iconAction="onClickAction"
        @click-icon-header="onClickAdd"
      />
    </template>
  </BoxWithTitle>
</template>

<script>
// TODO - {itemActions="actions" when permissions done}
import { cloneDeep } from 'lodash';
import Vue from 'vue';
import { mapGetters } from 'vuex';
import BoxWithTitle from '@/components/shared/containers/VBoxWithTitle';
import CouponStandardForm from '@/components/forms/CouponStandardForm.vue';
import ConfirmForm from '@/components/shared/widgets/ConfirmForm.vue';
import VTableLocal from '@/components/shared/content-views/VTableLocal';


export default {
    components: {
        VTableLocal,
        BoxWithTitle,
    },
    data() {
        return {
            standards: [],
            loading: false,
        };
    },
    computed: {
        ...mapGetters([
            'userLang',
            'hasPermission',
        ]),
        tableHeaders() {
            return [
                'networkType',
                'componentType',
                'coupon',
                'excellent',
                'veryGood',
                'good',
                'fair',
                'poor',
                'severe',
            ].map(header => ({
                text: this.$t(`standards.labels.${header}`),
                value: header,
            }));
        },
        actions() {
            const actions = [];
            if (this.hasPermission('standards.edit')) {
                actions.push({
                    name: this.$t('forms.btn.edit'),
                    icon: 'edit',
                    mode: 'edit',
                });
            }

            if (this.hasPermission('standards.remove')) {
                actions.push({
                    name: this.$t('forms.btn.delete'),
                    icon: 'delete',
                    mode: 'delete',
                    iconAndTextColor: '#EF4135',
                });
            }

            return actions;
        },
        displayedStandards() {
            return this.standards.map(standard => {
                const {
                    id, networkType, componentType, coupon, norm,
                } = standard;
                const excellent = `[ ${norm.excellent}, ${norm.veryGood} [`;
                const veryGood = `[ ${norm.veryGood}, ${norm.good} [`;
                const good = `[ ${norm.good}, ${norm.fair} [`;
                const fair = `[ ${norm.fair}, ${norm.poor} [`;
                const poor = `[ ${norm.poor}, ${norm.severe} [`;
                const severe = `${norm.severe}+`;
                return {
                    id,
                    networkType: networkType ? networkType.name : this.$t('forms.labels.any'),
                    componentType: componentType ? componentType.name : this.$t('forms.labels.any'),
                    coupon: coupon.name,
                    excellent,
                    veryGood,
                    good,
                    fair,
                    poor,
                    severe,
                };
            });
        },
        couponTypes() {
            return this.standards.map(({ couponType }) => couponType.id);
        },
    },
    async created() {
        await this.getStandards();
    },
    methods: {
        async getStandards() {
            this.loading = true;
            const queryParams = { standardFamily: this.$route.params.standardFamilyId };
            const { data } = await this.$rq.getStandards(queryParams);
            this.standards = data.standards.filter(standard => standard.coupon !== undefined);
            this.loading = false;
        },
        onClickAdd() {
            this.$root.$emit('showModal', {
                title: this.$t('standards.addStandard'),
                component: Vue.component('CouponStandardForm', CouponStandardForm),
                payload: {
                    standards: this.standards,
                },
                onConfirm: requestPayload => this.addStandardRequest(requestPayload),
            });
        },
        onClickAction(item, action) {
            if (action.mode === 'edit') {
                const standard = this.standards.find(({ id }) => id === item.id);
                const norm = {};
                if (item.excellent) norm.excellent = item.excellent.toString();
                if (item.veryGood) norm.veryGood = item.veryGood.toString();
                if (item.good) norm.good = item.good.toString();
                if (item.fair) norm.fair = item.fair.toString();
                if (item.poor) norm.poor = item.poor.toString();
                if (item.severe) norm.severe = item.severe.toString();
                const standardData = cloneDeep(standard);
                standardData.norm = norm;

                this.$root.$emit('showModal', {
                    title: this.$t('standards.editStandard'),
                    component: Vue.component('CouponStandardForm', CouponStandardForm),
                    validateAtInitialization: true,
                    payload: {
                        standard: standardData,
                        isEdition: true,
                        standards: this.standards,
                    },
                    onConfirm: requestPayload => this.updateStandardRequest(item.id, requestPayload),
                });
            } else if (action.mode === 'delete') {
                this.$root.$emit('showModal', {
                    title: this.$t('standards.removeStandard'),
                    component: Vue.component('confirmRemoveStandardForm', ConfirmForm),
                    payload: { msg: this.$t('standards.confirmRemoveStandard') },
                    onConfirm: () => this.deleteStandardRequest(item.id),
                    isForm: false,
                });
            }
        },
        preparePayload(payload) {
            const requestPayload = cloneDeep(payload);
            if (requestPayload.networkType === null) {
                delete requestPayload.networkType;
            }
            if (requestPayload.componentType === null) {
                delete requestPayload.componentType;
            }
            return requestPayload;
        },
        async addStandardRequest(payload) {
            const requestPayload = this.preparePayload(payload);
            requestPayload.standardFamily = this.$route.params.standardFamilyId;

            await this.$rq.addStandard(requestPayload);
            this.$toasted.global.toast_success({
                message: this.$t('msg.standardCreated'),
            });
            await this.getStandards();
        },
        async updateStandardRequest(standardId, payload) {
            const requestPayload = this.preparePayload(payload);
            await this.$rq.updateStandard(standardId, requestPayload);
            this.$toasted.global.toast_success({
                message: this.$t('msg.standardModified'),
            });
            await this.getStandards();
        },
        async deleteStandardRequest(standardId) {
            await this.$rq.deleteStandard(standardId);
            this.$toasted.global.toast_success({
                message: this.$t('msg.standardRemoved'),
            });
            await this.getStandards();
        },
    },
};
</script>
